import SalesOfficeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity';
import { Component, Inject, Input } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { SalesOfficeService } from '../../configuration/sales-office/sales-office.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ToggleOptionComponent } from 'app/shared/components/form-ui/toggle-option/toggle-option.component';
import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';
import { DialogDataAccountSalesBase, IBaseAccountingAttributes, PayloadAccountSalesBasic, PayloadAccountSalesMulti} from './interfaces/account-sales.interface';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';

@Component({
  selector: 'app-account-sales-offices',
  templateUrl: './account-sales.component.html',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatIcon,
    MatButtonModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    NgxMatSelectSearchModule,
    MatSlideToggleModule,
    MatExpansionModule,
    ToggleOptionComponent,
    FormSectionComponent
  ],
})
export class SalesOfficesFormComponent<
  TAttributes,
  TCreate = PayloadAccountSalesBasic | PayloadAccountSalesMulti,
  TUpdate = PayloadAccountSalesBasic | PayloadAccountSalesMulti
> 
{
  @Input() multiMode = false;

  formArray = new FormArray<FormGroup>([]);
  salesOfficeList: SalesOfficeAttributes[] = []
  dataSource: FormGroup[] = [];
  displayedColumns: string[] = [];

  expandedIndex: number | null = null;

  constructor(
    private readonly salesOfficeService: SalesOfficeService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<SalesOfficesFormComponent<TAttributes, TCreate, TUpdate>>,
    @Inject(MAT_DIALOG_DATA) public data: DialogDataAccountSalesBase<TAttributes, TCreate, TUpdate>
  ) {
    this.multiMode = !!data.multiMode;
  }

  ngAfterViewInit(): void {
    this.displayedColumns = this.getDisplayedColumns();
    this.fetchAllSalesOffice();
    this.getList();
  }

  getDisplayedColumns(): string[] {
    const base = ['salesOffice', 'account', 'costCenter', 'postingKey', 'statusId'];
    const extras = [
      'com_accountingAccount', 'com_costCenter', 'com_accountingKey',
      'ret_accountingAccount', 'ret_costCenter', 'ret_accountingKey',
    ];
    return [...base, ...(this.multiMode ? extras : []), 'actions'];
  }

  getList(): void {
    this.formArray.clear();

    const hasFather = !!this.data.fatherId;
    const elementId = String(this.data.element._id);

    const serviceGet = hasFather
      ? this.data.crudService.getAllList(this.data.fatherId, elementId)
      : this.data.crudService.getAllList(elementId);

    serviceGet.subscribe({
      next: (res) => {
        res.data.forEach((item) => {
          const group = this.multiMode ? this.createMultiDetailRow(item) : this.createRow(item);
          this.formArray.push(group);
        });
        this.dataSource = [...this.formArray.controls];
      },
      error: () => {
        this.snackBar.open("Error al cargar determinación de cuentas", "Cerrar", { duration: 4000 });
      }
    });
  }

  private fetchAllSalesOffice(): void {
    this.salesOfficeService
      .findToComboBox()
      .subscribe({
        next: (res) => {
          this.salesOfficeList = res.data;
        },
        error: (err) => {
          this.snackBar.open(err.error.message || "Error al traer oficinas de venta.", "Cerrar", { duration: 5000 });
        },
      });
  }

  getOfficeName(officeId: string): string {
    const office = this.salesOfficeList.find(o => String(o._id) === officeId);
    return office ? office.name : 'Sin oficina';
  }

  createRow(initialData?: IBaseAccountingAttributes): FormGroup {
    return this.fb.group({
      _id: [initialData?._id || null],
      salesOfficeId: [initialData?.salesOffice?._id || '', Validators.required],
      accountingAccount: [initialData?.accountingAccount || '', Validators.required],
      costCenter: [initialData?.costCenter || '', Validators.required],
      accountingKey: [initialData?.accountingKey || '', Validators.required],
      statusId: [initialData?.statusId ?? StatusEnum.INACTIVE]
    });
  }

  createMultiDetailRow(initialData?: IBaseAccountingAttributes): FormGroup {
    return this.fb.group({
      _id: [initialData?._id || null],
      salesOfficeId: [initialData?.salesOffice?._id || '', Validators.required],
      accountingAccount: [initialData?.accountingAccount || '', Validators.required],
      costCenter: [initialData?.costCenter || '', Validators.required],
      accountingKey: [initialData?.accountingKey || '', Validators.required],
      com_accountingAccount: [initialData?.com_accountingAccount || '', Validators.required],
      com_costCenter: [initialData?.com_costCenter || '', Validators.required],
      com_accountingKey: [initialData?.com_accountingKey || '', Validators.required],
      ret_accountingAccount: [initialData?.ret_accountingAccount || '', Validators.required],
      ret_costCenter: [initialData?.ret_costCenter || '', Validators.required],
      ret_accountingKey: [initialData?.ret_accountingKey || '', Validators.required],
      statusId: [initialData?.statusId === StatusEnum.ACTIVE ? true : false],
    });
  }

  addRow(): void {
    const group = this.multiMode ? this.createMultiDetailRow() : this.createRow();
    this.formArray.push(group);
    this.dataSource = [...this.formArray.controls];
    this.expandedIndex = this.dataSource.length - 1;
  }

  removeRow(index: number): void {
    const row = this.formArray.at(index);
    const id = row.get('_id')?.value;

    if (id) {
      const hasFather = !!this.data.fatherId;
      const delete$ = hasFather
        ? this.data.crudService.deleteEmbedded2d(this.data.fatherId, String(this.data.element._id), id)
        : this.data.crudService.deleteEmbedded1d(String(this.data.element._id), id);

      delete$.subscribe({
        next: () => this.getList(),
        error: () => {
          this.snackBar.open("Error al eliminar", "Cerrar", { duration: 4000 });
        }
      });
    } else {
      this.formArray.removeAt(index);
      this.dataSource = [...this.formArray.controls];
    }
  }

  saveRow(index: number): void {
    const row = this.formArray.at(index);
    if (row.invalid) return;

    const raw = row.getRawValue();

    const payload: PayloadAccountSalesBasic | PayloadAccountSalesMulti = this.multiMode
      ? {
          salesOfficeId: raw.salesOfficeId,
          accountingAccount: raw.accountingAccount,
          costCenter: raw.costCenter,
          accountingKey: raw.accountingKey,
          com_accountingAccount: raw.com_accountingAccount,
          com_costCenter: raw.com_costCenter,
          com_accountingKey: raw.com_accountingKey,
          ret_accountingAccount: raw.ret_accountingAccount,
          ret_costCenter: raw.ret_costCenter,
          ret_accountingKey: raw.ret_accountingKey,
          statusId: raw.statusId ? StatusEnum.ACTIVE : StatusEnum.INACTIVE,
        } 
      : {
          salesOfficeId: raw.salesOfficeId,
          accountingAccount: raw.accountingAccount,
          costCenter: raw.costCenter,
          accountingKey: raw.accountingKey,
          statusId: raw.statusId ?? StatusEnum.INACTIVE,
        }

    const hasFather = !!this.data.fatherId;

    const save$ = raw._id
      ? hasFather
        ? this.data.crudService.updateEmbedded2d(this.data.fatherId, String(this.data.element._id), raw._id, payload as TUpdate)
        : this.data.crudService.updateEmbedded(String(this.data.element._id), raw._id, payload)
      : hasFather
        ? this.data.crudService.createEmbedded2d(this.data.fatherId, String(this.data.element._id), payload as TCreate)
        : this.data.crudService.createEmbedded(String(this.data.element._id), payload as TCreate);

    save$.subscribe({
      next: () => {
        this.getList()
        this.expandedIndex = null;
      },
      error: (err) => {
        this.snackBar.open(err.error.message || "Error al guardar", "Cerrar", { duration: 4000 });
      }
    });
  }

  onStatusChange(value: boolean, index: number): void {
    const statusId = value ? StatusEnum.ACTIVE : StatusEnum.INACTIVE;
    this.formArray.at(index).get('statusId')?.setValue(statusId);
  }

  close(): void {
    this.dialogRef.close();
  }
}
